<template>
  <aside>
    <transition name="backdrop">
      <div v-if="collapsed" class="sb-backdrop" @click="toggleSidebar" aria-hidden="true" />
    </transition>

    <nav id="adm-sidebar" class="sb" :class="{ 'sb--open': collapsed, 'sb--slim': desktopCollapsed }" :inert="(isMobile && !collapsed) || null" aria-label="Navegación principal">
      <!-- ── Brand ─────────────────────────── -->
      <div class="sb__brand">
        <router-link to="/admin" class="sb__brand-link" aria-label="Ir al inicio">
          <span class="sb__wordmark">
            <span class="sb__wm-main">Buzón Digital</span>
            <span class="sb__wm-sub">Laboral</span>
          </span>
        </router-link>

        <button class="sb__toggle" @click="toggleDesktopSidebar" :aria-expanded="desktopCollapsed ? 'false' : 'true'" :aria-label="desktopCollapsed ? 'Expandir menú' : 'Contraer menú'">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <polyline v-if="desktopCollapsed" points="9 18 15 12 9 6" />
            <polyline v-else points="15 18 9 12 15 6" />
          </svg>
        </button>

        <button ref="closeBtn" class="sb__close" @click="toggleSidebar" aria-label="Cerrar menú">
          <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <!-- ── Navegación ─────────────────────── -->
      <div class="sb__nav">
        <template v-for="(group, gi) in groupedMenuItems" :key="gi">
          <span v-if="group.label && !desktopCollapsed" class="sb__group-label">{{ group.label }}</span>
          <div v-else-if="gi > 0" class="sb__group-sep" role="separator" />
          <ul class="sb__list">
            <SidebarLink v-for="(item, i) in group.items" :key="i" :to="item.link" :icon="item.icon" :name="item.name" :slim="desktopCollapsed" :badge="item.badgeKey === 'pending' ? pendingCount : 0">
              {{ item.name }}
            </SidebarLink>
          </ul>
        </template>
      </div>

      <!-- ── Footer: identidad (empresa / usuario) ──────── -->
      <div class="sb__foot">
        <div v-if="users" class="sb__foot-card" :class="{ 'sb__foot-card--slim': desktopCollapsed }" :title="desktopCollapsed ? footName : undefined">
          <!-- Avatar: logo de empresa o iniciales -->
          <div class="sb__avatar" :class="{ 'sb__avatar--img': companyLogoUrl }" aria-hidden="true">
            <img v-if="companyLogoUrl" :src="companyLogoUrl" alt="" @error="companyImgError = true" />
            <span v-else>{{ footInitials }}</span>
          </div>
          <!-- Sin v-show: CSS transiciona suavemente -->
          <div class="sb__user-meta">
            <span class="sb__user-name">{{ footName }}</span>
            <span class="sb__user-email">{{ footSub }}</span>
          </div>
        </div>
      </div>
    </nav>
  </aside>
</template>

<script>
import SidebarLink from "./SidebarLink";
import { UsersStorage } from "@/db/storage";
import { collapsed, toggleSidebar, desktopCollapsed, toggleDesktopSidebar } from "./state";
import authService from "@/services/auth.service";
import { AdminService } from "@/services";

const ICONS = {
  home: '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
  import: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>',
  messages: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  users: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  settings:
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  building: '<path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/>',
  multiuser: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  stats: '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
};

export default {
  name: "SidebarComponent",
  components: { SidebarLink },

  data() {
    return {
      usersStorage: new UsersStorage(),
      users: null,
      company: null,
      pendingCount: 0,
      companyImgError: false,
      isMobile: false,
      dataMenu: [
        { name: "Inicio", icon: ICONS.home, link: "/admin", roles: ["admin", "superadmin", "manager", "reader"], group: "main" },
        { name: "Mensajes Recibidos", icon: ICONS.messages, link: "/admin/messages", roles: ["admin", "superadmin", "manager", "reader"], group: "main", badgeKey: "pending" },
        { name: "Importar Mensajes", icon: ICONS.import, link: "/admin/import/messages", roles: ["superadmin"], group: "main" },
        { name: "Estadísticos", icon: ICONS.stats, link: "/estadisticos", roles: ["admin", "reader"], group: "main" },
        { name: "Usuarios", icon: ICONS.users, link: "/admin/administracion", roles: ["admin"], group: "config", groupLabel: "Configuración" },
        { name: "Opciones", icon: ICONS.settings, link: "/admin/configuracion", roles: ["admin"], group: "config" },
        { name: "Multiusuarios", icon: ICONS.multiuser, link: "/admin/configuracion/administradores", roles: ["admin"], group: "config" },
        { name: "Empresas", icon: ICONS.building, link: "/admin/configuracion/empresas", roles: ["superadmin"], group: "config", groupLabel: "Configuración" },
      ],
    };
  },

  setup() {
    return { collapsed, toggleSidebar, desktopCollapsed, toggleDesktopSidebar };
  },

  computed: {
    groupedMenuItems() {
      if (!this.users) return [];
      const visible = this.dataMenu.filter((item) => item.roles.includes(this.users.rol));
      const map = {};
      for (const item of visible) {
        const k = item.group || "main";
        if (!map[k]) map[k] = { label: item.groupLabel || "", items: [] };
        map[k].items.push(item);
      }
      return Object.values(map);
    },
    userEmail() {
      return this.users?.email || "";
    },
    userLabel() {
      return this.users?.fullname || this.users?.email || "Usuario";
    },
    userInitials() {
      const src = this.users?.fullname || this.users?.email || "?";
      const parts = src
        .trim()
        .split(/[\s@.]+/)
        .filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return src[0].toUpperCase();
    },
    companyName() {
      return this.company?.name_trade || this.company?.name || "";
    },
    companyLogoUrl() {
      if (this.companyImgError) return null;
      return this.company?.photo?.link || this.company?.logo || null;
    },
    companyInitials() {
      const src = this.companyName || "?";
      const parts = src.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return src.substring(0, 2).toUpperCase();
    },
    isSuperadmin() {
      return this.users?.rol === "superadmin";
    },
    // Identidad del footer: superadmin = plataforma; resto = empresa
    footName() {
      return this.isSuperadmin ? "Buzón Digital Laboral" : this.companyName || this.userLabel;
    },
    footSub() {
      return this.isSuperadmin ? "HR Outservice" : this.userEmail;
    },
    footInitials() {
      return this.isSuperadmin ? "BD" : this.companyInitials || this.userInitials;
    },
  },

  watch: {
    $route() {
      if (collapsed.value) toggleSidebar();
    },
    // Drawer móvil abierto: bloquear scroll de fondo y mover el foco dentro
    collapsed(open) {
      if (!this.isMobile) return;
      if (open) {
        document.body.style.overflow = "hidden";
        this.$nextTick(() => this.$refs.closeBtn && this.$refs.closeBtn.focus());
      } else {
        document.body.style.overflow = "";
      }
    },
  },

  mounted() {
    this.loadUserData();
    window.addEventListener("storage", this.onStorage);
    this._mql = window.matchMedia("(max-width: 1023px)");
    this.isMobile = this._mql.matches;
    this._onMql = (e) => {
      this.isMobile = e.matches;
      if (!e.matches) document.body.style.overflow = "";
    };
    this._mql.addEventListener("change", this._onMql);
    document.addEventListener("keydown", this.onKeydown);
  },

  beforeUnmount() {
    window.removeEventListener("storage", this.onStorage);
    if (this._mql) this._mql.removeEventListener("change", this._onMql);
    document.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = "";
  },

  methods: {
    async loadUserData() {
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          this.users = user;
          const raw = localStorage.getItem("company");
          this.company = raw ? JSON.parse(raw) : user.company || null;
          if (user.rol === "superadmin") this.fetchPendingCount();
        }
      } catch {
        this.users = this.usersStorage.get();
        const raw = localStorage.getItem("company");
        this.company = raw ? JSON.parse(raw) : null;
        if (this.users?.rol === "superadmin") this.fetchPendingCount();
      }
    },
    async fetchPendingCount() {
      try {
        const { data } = await AdminService.listMessage({ pending: "true", perpage: 1, page: 1 });
        this.pendingCount = data.pages || 0;
      } catch {
        this.pendingCount = 0;
      }
    },
    onStorage(e) {
      if (["user", "company", null].includes(e.key)) this.loadUserData();
    },
    onKeydown(e) {
      if (e.key === "Escape" && collapsed.value) toggleSidebar();
    },
  },
};
</script>

<style scoped>
.sb {
  --w: 250px;
  --w-slim: 68px;

  /* ── Brand-consistent tokens ── */
  --bg: #ffffff;
  --border: #eaecf0;
  --accent: #355e91; /* navy de marca (era #2563eb — inconsistente) */
  --accent-d: #2d5080;
  --accent-light: rgba(53, 94, 145, 0.08);
  --text: #111827;
  --text-muted: #6b7280;
  --text-dim: #9ca3af;
  --hover: rgba(53, 94, 145, 0.05); /* hover con tinte navy en lugar de gris neutro */

  --ease: cubic-bezier(0.4, 0, 0.2, 1);
  --radius: 10px;
}

/* ── Backdrop ── */
.sb-backdrop {
  display: none;
}

@media (max-width: 1023px) {
  .sb-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.38);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 1001;
    cursor: pointer;
  }
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

/* ── Panel ── */
.sb {
  position: fixed;
  inset: 0 auto 0 0;
  width: var(--w);
  background: var(--bg);
  display: flex;
  flex-direction: column;
  z-index: 1002;
  overflow: hidden;
  /* Sombra en capas: cercana suave + difusa larga */
  box-shadow: 1px 0 0 var(--border), 4px 0 20px rgba(0, 0, 0, 0.04), 8px 0 32px rgba(0, 0, 0, 0.03);
  transition: width 0.16s var(--ease);
}

.sb.sb--slim {
  width: var(--w-slim);
}

@media (max-width: 1023px) {
  .sb {
    width: 268px !important;
    transform: translateX(-110%);
    /* En mobile animamos transform (GPU), no width */
    transition: transform 0.28s var(--ease);
    /* Promovemos la capa para evitar el tirón del primer despliegue */
    will-change: transform;
    box-shadow: none;
  }
  .sb.sb--open {
    transform: translateX(0);
    box-shadow: 12px 0 40px rgba(0, 0, 0, 0.14);
  }
}

/* ── Brand ── */
.sb__brand {
  height: 60px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 14px;
  gap: 8px;
  background: #355e91;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.sb__brand-link {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  margin: 0;
  border-radius: 0;
  text-decoration: none;
  overflow: hidden;
  transition: opacity 0.12s, flex 0.16s var(--ease), width 0.16s var(--ease);
}

.sb__brand-link:hover {
  opacity: 0.8;
}
.sb__brand-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 4px;
}

.sb__wordmark {
  display: flex;
  flex-direction: column;
  gap: 1px;
  overflow: hidden;
  max-width: 160px;
  opacity: 1;
  transition: max-width 0.16s var(--ease), opacity 0.12s ease;
}

.sb--slim .sb__wordmark {
  max-width: 0;
  opacity: 0;
  pointer-events: none;
}

.sb__wm-main {
  font-size: 14px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.02em;
  white-space: nowrap;
  line-height: 1.15;
}
.sb__wm-sub {
  font-size: 9.5px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.82);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  white-space: nowrap;
  line-height: 1;
}

/* Toggle (desktop) */
.sb__toggle {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.15s;
}

.sb__toggle:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.4);
}
.sb__toggle:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

/* Close (mobile) */
.sb__close {
  display: none;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s;
}

.sb__close:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.sb__close:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.6);
  outline-offset: 2px;
}

/* Slim brand */
.sb--slim .sb__brand {
  padding: 0;
  justify-content: center;
  gap: 0;
}

.sb--slim .sb__brand-link {
  display: none;
}

.sb--slim .sb__toggle {
  margin: 0;
}

@media (max-width: 1023px) {
  .sb__toggle {
    display: none;
  }
  .sb__close {
    display: flex;
  }
  .sb--slim .sb__brand {
    padding: 0 14px;
    justify-content: flex-start;
    gap: 8px;
  }
  .sb--slim .sb__brand-link {
    flex: 1;
    width: auto;
    padding: 6px 8px;
    margin: 0 -8px;
    pointer-events: auto;
  }
  .sb--slim .sb__wordmark {
    max-width: 160px;
    opacity: 1;
  }
}

/* ── Navegación ── */
.sb__nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 10px 0;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}

.sb__nav::-webkit-scrollbar {
  width: 3px;
}
.sb__nav::-webkit-scrollbar-thumb {
  background: rgba(53, 94, 145, 0.18); /* scrollbar con tinte navy */
  border-radius: 3px;
}

.sb--slim .sb__nav {
  padding: 10px 9px 0;
}
@media (max-width: 1023px) {
  .sb--slim .sb__nav {
    padding: 10px 10px 0;
  }
}

.sb__group-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6b7280; /* AA sobre blanco */
  padding: 16px 8px 5px;
  user-select: none;

  /* Línea decorativa antes del label */
  &::before {
    content: "";
    display: block;
    width: 14px;
    height: 1px;
    background: #d4dae2;
    flex-shrink: 0;
  }
}

.sb__group-sep {
  height: 1px;
  background: var(--border);
  margin: 10px 4px;
}

.sb__list {
  list-style: none;
  padding: 0;
  margin: 0 0 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

/* ── Footer ── */
.sb__foot {
  flex-shrink: 0;
  padding: 10px;
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #fafbfc; /* muy ligero off-white para separar visualmente del nav */
}

/* En slim: igualar el padding lateral al del nav (8px) para que el avatar
   se centre en el mismo punto que los íconos del menú */
.sb--slim .sb__foot {
  padding: 9px; /* igual al padding lateral del nav en slim (9px) */
}

.sb__foot-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid var(--border);
  overflow: visible;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #c9d0db;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.07);
  }
}

.sb__foot-card--slim {
  justify-content: center;
  padding: 6px 0;
  gap: 0; /* elimina el espacio fantasma entre items colapsados a width:0 */
  background: transparent;
  border-color: transparent;
}

/* En slim: colapsar user-meta y dots sin v-show */
.sb__foot-card--slim .sb__user-meta {
  max-width: 0;
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 1023px) {
  .sb__foot-card--slim {
    justify-content: flex-start;
    padding: 10px 12px;
    background: var(--hover);
    border-color: var(--border);
  }
}

.sb__avatar {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 9px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: 0.04em;
  overflow: hidden;

  &--img {
    background: #f3f4f6;
    border: 1.5px solid #e5e7eb;
    padding: 3px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 5px;
  }
}

.sb__user-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  /* Transición CSS en lugar de v-show */
  max-width: 160px;
  opacity: 1;
  transition: max-width 0.16s var(--ease), opacity 0.12s ease;
}

.sb__user-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.sb__user-email {
  font-size: 11px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}
</style>
